package link.erpango.quip.data.remote.repository.extraction

import io.ktor.client.network.sockets.ConnectTimeoutException
import io.ktor.client.network.sockets.SocketTimeoutException
import io.ktor.client.plugins.HttpRequestTimeoutException
import io.ktor.client.plugins.ResponseException
import io.ktor.client.plugins.timeout
import io.ktor.client.request.request
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import kotlinx.coroutines.CancellationException
import link.erpango.quip.QuipConstants
import link.erpango.quip.data.remote.ApiClient
import link.erpango.quip.data.remote.repository.IApiRepository
import link.erpango.quip.data.remote.ktor.IHttpRequest
import link.erpango.quip.domain.entities.DExtractionError
import link.erpango.quip.domain.entities.DExtractionErrorBody
import link.erpango.quip.domain.entities.DExtractionOutcome
import link.erpango.quip.domain.entities.DExtractionRequest
import link.erpango.quip.domain.entities.DExtractionResponse
import link.erpango.quip.domain.entities.DExtractionStatus
import link.erpango.quip.utils.FileUtils

/**
 * Cliente de `POST api/extract-vehicles/` y `GET api/extract-vehicles/status/`.
 *
 * Como en [link.erpango.quip.data.remote.repository.myspareparts.MySparePartsRepository.shareMySparePart],
 * la respuesta se gestiona a mano en lugar de con `.response()`:
 * - `.response()` escribe el cuerpo de la respuesta en el log, y aquí contiene lo que el
 *   usuario escribió en su nota. Este repositorio solo registra código de estado y `code`.
 * - Los errores del backend (429 con `retry_after`, 501, 504...) se devuelven tipados.
 * - La cancelación se relanza para que el botón "Cancelar" corte la petición.
 */
class ExtractionRepository(
    override val api: ApiClient,
) : IExtractionRepository, IApiRepository<ApiClient> {
    override val resourcePath: String
        get() = "api/extract-vehicles/"

    override suspend fun extract(note: String): DExtractionOutcome {
        val httpRequest = api.consume(resourcePath)
            .post(DExtractionRequest(note), DExtractionRequest.serializer())
        // The backend may spend minutes on a reasoning LLM: far longer than the default 20 s.
        httpRequest.request.timeout {
            requestTimeoutMillis = QuipConstants.Http.extractionTimeout
            socketTimeoutMillis = QuipConstants.Http.extractionTimeout
        }
        return try {
            val response = httpRequest.httpClient.request(httpRequest.request)
            if (response.status.value in 200..299) parseSuccess(response) else failure(response)
        } catch (e: ResponseException) {
            // With expectSuccess=true the client throws for every 4xx/5xx.
            failure(e.response)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            DExtractionOutcome.Failure(e.toExtractionError())
        }
    }

    override suspend fun status(): DExtractionStatus? {
        val httpRequest: IHttpRequest = api.consume("${resourcePath}status/").get()
        return try {
            val response = httpRequest.httpClient.request(httpRequest.request)
            api.json.decodeFromString(DExtractionStatus.serializer(), response.bodyAsText())
        } catch (e: ResponseException) {
            // 404: backend without the endpoint yet -> the assistant is simply not available.
            if (e.response.status.value == 404) DExtractionStatus(enabled = false) else null
        } catch (e: CancellationException) {
            throw e
        } catch (_: Throwable) {
            null
        }
    }

    private suspend fun parseSuccess(response: HttpResponse): DExtractionOutcome {
        return try {
            DExtractionOutcome.Success(
                api.json.decodeFromString(DExtractionResponse.serializer(), response.bodyAsText())
            )
        } catch (e: CancellationException) {
            throw e
        } catch (_: Throwable) {
            FileUtils.log("extract-vehicles: unreadable response (${response.status.value})")
            DExtractionOutcome.Failure(DExtractionError.Unknown)
        }
    }

    private suspend fun failure(response: HttpResponse): DExtractionOutcome.Failure {
        val status = response.status.value
        val body = try {
            api.json.decodeFromString(DExtractionErrorBody.serializer(), response.bodyAsText())
        } catch (e: CancellationException) {
            throw e
        } catch (_: Throwable) {
            DExtractionErrorBody()
        }
        val retryAfter = body.retryAfter
            ?: response.headers["Retry-After"]?.trim()?.toIntOrNull()
            ?: DEFAULT_RETRY_AFTER_SECONDS
        FileUtils.log("extract-vehicles: failed status=$status code=${body.code}")
        return DExtractionOutcome.Failure(errorFor(status, body.code, retryAfter))
    }

    private fun errorFor(status: Int, code: String?, retryAfter: Int): DExtractionError = when {
        code == "rate_limited" -> DExtractionError.RateLimited(retryAfter)
        code == "daily_quota_exceeded" -> DExtractionError.DailyQuotaExceeded(retryAfter)
        code == "global_quota_exceeded" -> DExtractionError.GlobalQuotaExceeded(retryAfter)
        code == "note_too_long" -> DExtractionError.NoteTooLong
        code == "empty_note" -> DExtractionError.EmptyNote
        status == 429 -> DExtractionError.RateLimited(retryAfter)
        status == 401 || status == 403 -> DExtractionError.Unauthorized
        status == 404 || status == 501 -> DExtractionError.Unavailable
        status == 422 -> DExtractionError.ExtractionFailed
        status == 502 || status == 503 -> DExtractionError.ProviderError
        status == 504 -> DExtractionError.Timeout
        else -> DExtractionError.Unknown
    }

    private fun Throwable.toExtractionError(): DExtractionError = when (this) {
        is HttpRequestTimeoutException, is SocketTimeoutException, is ConnectTimeoutException -> DExtractionError.Timeout
        else -> DExtractionError.Network
    }

    private companion object {
        const val DEFAULT_RETRY_AFTER_SECONDS = 60
    }
}
